import { useEffect, useRef, useState } from "react";

export default function HeroSection() {
  const ref = useRef<HTMLDivElement>(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setVisible(true);
          observer.disconnect();
        }
      },
      { threshold: 0.15 }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, []);

  return (
    <section id="home" className="relative bg-gradient-to-br from-white via-gray-50 to-white min-h-[75vh] md:min-h-[85vh] pb-8 md:pb-10 scroll-mt-20">
      {/* Subtle background pattern */}
      <div className="absolute inset-0 opacity-[0.03]" style={{
        backgroundImage: `radial-gradient(circle at 1px 1px, #94a3b8 1px, transparent 0)`,
        backgroundSize: '40px 40px'
      }} />

      <div ref={ref} className="relative z-10 mx-auto px-6 lg:px-16 w-full max-w-7xl pt-14 md:pt-16 lg:pt-20">

        {/* Main content grid */}
        <div className="grid lg:grid-cols-2 gap-4 lg:gap-8 items-start">

          {/* Left — copy */}
          <div className={`flex flex-col justify-start transition-all duration-700 ${visible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`}>
            {/* Trust badge */}
            <div className="inline-flex items-center gap-1.5 bg-blue-50 rounded-full px-3 py-1.5 w-fit mb-3">
              <i className="ri-shield-check-line text-blue-600 text-sm" />
              <span className="text-xs font-black text-blue-600">FDA-Cleared Medical Grade</span>
            </div>

            <h1 className="text-4xl md:text-5xl lg:text-[3.25rem] font-extrabold text-gray-900 mb-3 leading-[1.1]">
              <span className="block">Pure Sanitized Air</span>
              <span className="block">No Replacement Filters</span>
              <span className="block text-xl md:text-2xl lg:text-3xl font-bold text-red-600 mt-1 italic">Ever.</span>
            </h1>

            <p className="text-sm md:text-base text-gray-700 font-bold leading-relaxed max-w-lg mb-4">
              Protect your family with hospital-grade defense. Destroys viruses, bacteria, and wildfire smoke—without costly replacement filters.
            </p>

            {/* Trust stats row */}
            <div className="flex items-center gap-4 flex-wrap">
              <div className="flex items-center gap-1.5">
                <div className="flex -space-x-0.5">
                  {[1,2,3,4,5].map(i => (
                    <i key={i} className="ri-star-fill text-amber-400 text-sm" />
                  ))}
                </div>
                <span className="text-sm font-bold text-gray-800">4.9/5</span>
                <span className="text-xs text-gray-700 font-bold">(2,400+ reviews)</span>
              </div>
              <div className="w-px h-4 bg-gray-300 hidden sm:block" />
              <div className="flex items-center gap-1.5">
                <i className="ri-global-line text-gray-400 text-sm" />
                <span className="text-sm font-bold text-gray-700">Trusted in 50+ countries</span>
              </div>
            </div>

            <p className="text-xs text-gray-700 font-bold mt-2 italic">
              See why over 1 million customers worldwide trust Airdog — backed by a 4.9/5 star average across more than 2,400 verified reviews.
            </p>
          </div>

          {/* Right — product */}
          <div className={`relative flex items-center justify-center transition-all duration-700 delay-150 ${visible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`}>
            <div className="relative w-full flex items-center justify-center">
              <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[80%] h-[80%] bg-red-500/5 rounded-full blur-3xl" />

              <img
                src="https://storage.readdy-site.link/project_files/0ab4d894-9a87-4492-8cdb-8328f902642e/be0086ea-a4a0-4b10-92fd-88750f011917_cascades.jpg?v=ae1be1f8e7fd764aa1d982af7e396ccc"
                alt="Airdog air purifier"
                className="relative w-auto h-[50vh] lg:h-[58vh] max-w-full object-contain drop-shadow-2xl"
              />

              {/* Floating badges */}
              <div className="absolute top-[5%] left-0 md:left-2 bg-white rounded-xl px-4 py-3 shadow-lg border border-gray-100">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-lg bg-red-100 flex items-center justify-center">
                    <i className="ri-virus-line text-red-600 text-base" />
                  </div>
                  <div>
                    <p className="text-sm font-black text-gray-900">99.9%</p>
                    <p className="text-[11px] text-gray-700 font-bold">Virus removal</p>
                  </div>
                </div>
              </div>

              <div className="absolute bottom-[10%] right-0 md:right-2 bg-white rounded-xl px-4 py-3 shadow-lg border border-gray-100">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-lg bg-red-500/10 flex items-center justify-center">
                    <i className="ri-recycle-line text-red-500 text-base" />
                  </div>
                  <div>
                    <p className="text-sm font-black text-gray-900">$0</p>
                    <p className="text-[11px] text-gray-700 font-bold">Filter costs</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

    </section>
  );
}